import { differenceInCalendarDays, startOfDay } from "date-fns";

export type ScoreLoan = {
  id: string;
  clientId: string;
  amount: number;
  dueDate: Date;
  loanDate: Date;
  status: "paid" | "active" | "overdue";
  interestType: "monthly" | "fixed" | "daily";
};

export type ScorePayment = {
  loanId: string;
  paidAt: Date;
  interestAmount: number;
};

export type ScoreResult = {
  score: number;
  label: "Bom pagador" | "Atenção" | "Alto risco";
  tone: "success" | "warning" | "destructive";
  activeLoansCount: number;
  maxOverdueDays: number;
};

/**
 * Calcula o score (0–100) de um cliente baseado em histórico de empréstimos e pagamentos.
 * Regras:
 *  - começa em 100
 *  - atraso 1–7 dias: -10 / 8–30: -25 / >30: -40 (usa pior atraso ativo)
 *  - mais de 1 empréstimo ativo: -20
 *  - reincidência de atraso (cada empréstimo pago após data): -10
 *  - cada mês pagando juros em dia: +5
 *  - cada dívida liquidada: +10
 */
export function calculateClientScore(
  clientId: string,
  loans: ScoreLoan[],
  payments: ScorePayment[]
): ScoreResult {
  const today = startOfDay(new Date());
  const clientLoans = loans.filter((l) => l.clientId === clientId);
  const loanIds = new Set(clientLoans.map((l) => l.id));
  const clientPayments = payments.filter((p) => loanIds.has(p.loanId));

  let score = 100;

  const activeLoans = clientLoans.filter(
    (l) => l.status === "active" || l.status === "overdue"
  );

  // Pior atraso atual
  let maxOverdueDays = 0;
  for (const l of activeLoans) {
    if (l.dueDate < today) {
      const d = differenceInCalendarDays(today, l.dueDate);
      if (d > maxOverdueDays) maxOverdueDays = d;
    }
  }
  if (maxOverdueDays > 30) score -= 40;
  else if (maxOverdueDays >= 8) score -= 25;
  else if (maxOverdueDays >= 1) score -= 10;

  // Mais de um empréstimo ativo
  if (activeLoans.length > 1) score -= 20;

  // Reincidência: empréstimos pagos cujo último pagamento ocorreu após a due_date
  let lateRepayments = 0;
  let onTimePaidLoans = 0;
  const paidLoans = clientLoans.filter((l) => l.status === "paid");
  for (const l of paidLoans) {
    const lastPay = clientPayments
      .filter((p) => p.loanId === l.id)
      .sort((a, b) => b.paidAt.getTime() - a.paidAt.getTime())[0];
    if (lastPay) {
      if (startOfDay(lastPay.paidAt) > startOfDay(l.dueDate)) {
        lateRepayments += 1;
      } else {
        onTimePaidLoans += 1;
      }
    }
  }
  score -= lateRepayments * 10;

  // Bônus: cada dívida liquidada +10
  score += paidLoans.length * 10;

  // Bônus: cada pagamento de juros em dia (paidAt <= dueDate do empréstimo daquele momento)
  // Aproximação: contar pagamentos de juros (interestAmount > 0) em empréstimos sem atraso atual
  const onTimeInterestPayments = clientPayments.filter(
    (p) => p.interestAmount > 0
  ).length;
  score += Math.min(onTimeInterestPayments, 6) * 5;

  // Bônus extra leve por liquidações 100% em dia
  score += onTimePaidLoans * 2;

  if (score > 100) score = 100;
  if (score < 0) score = 0;

  let label: ScoreResult["label"];
  let tone: ScoreResult["tone"];
  if (score >= 80) {
    label = "Bom pagador";
    tone = "success";
  } else if (score >= 50) {
    label = "Atenção";
    tone = "warning";
  } else {
    label = "Alto risco";
    tone = "destructive";
  }

  return {
    score: Math.round(score),
    label,
    tone,
    activeLoansCount: activeLoans.length,
    maxOverdueDays,
  };
}
